/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from "react";
import debounce from "lodash/debounce";
import TableInstance from "./table-instance";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MdInsights } from "react-icons/md";
import { RouteConstants } from "../../config/constants/route";
import { GetSortBy } from "../../utils/sortby";
import GetAllCarts from "../../hooks/fetch-hooks/fetch-all/get-all-carts";
import { CART_STATUSES } from "./Constants";

const SEARCH_DEBOUNCE_MS = 300;

const useQuery = () => {
    return new URLSearchParams(useLocation().search);
};

function CartsTable() {
    const query = useQuery();
    const navigate = useNavigate();
    // eslint-disable-next-line
    const [searchParams, setSearchParams] = useSearchParams();

    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageSort, setPageSort] = useState([]);
    const [pageNumber, setPageNumber] = useState(query.get("page") || 1);
    const [pageSize] = useState(50);
    const [userId, setUserId] = useState(query.get("userId") || "");
    const [debouncedUserId, setDebouncedUserId] = useState(query.get("userId") || "");
    const [status, setStatus] = useState("");
    const [order, setOrder] = useState("DESC");
    const [orderBy, setOrderBy] = useState();

    useEffect(() => {
        if (pageSort.length > 0) {
            setOrder(pageSort[0].desc ? "DESC" : "ASC");
            setOrderBy(GetSortBy(pageSort[0].id));
        } else {
            setOrder(undefined);
            setOrderBy(undefined);
        }
    }, [pageSort]);

    const debouncedSetUserId = useMemo(
        () =>
            debounce((val) => {
                setDebouncedUserId(val);
                setPageNumber(1);
            }, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => debouncedSetUserId.cancel(), [debouncedSetUserId]);

    const getData = async () => {
        const response = await GetAllCarts({
            pageNumber,
            pageSize,
            status: status || undefined,
            userId: debouncedUserId || undefined,
            order,
            orderBy,
        });
        const rows = response?.data?.data?.rows || [];
        setTableData(rows);
        setTotal(response?.data?.data?.count || 0);
    };

    useEffect(() => {
        getData();
    }, [pageNumber, pageSize, debouncedUserId, status, order, orderBy]);

    useEffect(() => {
        setSearchParams({
            page: pageNumber,
            size: pageSize,
            userId: debouncedUserId,
        });
    }, [pageNumber, pageSize, debouncedUserId]);

    const maxPages = Math.ceil(total / pageSize);

    return (
        <>
            <div className="flex justify-end mb-3">
                <button
                    type="button"
                    onClick={() => navigate(RouteConstants.CART_REVIEW)}
                    title="Cart Review"
                    aria-label="Cart Review"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-slate-300 text-[#069079] font-sans font-semibold hover:bg-slate-50"
                >
                    <MdInsights style={{ fontSize: "20px" }} />
                    Cart Review
                </button>
            </div>
            <div className="flex gap-4 items-center relative">
                <input
                    value={userId}
                    className="px-6 font-sans w-[60%] bg-slate-100 py-4"
                    onChange={(e) => {
                        const next = e.target.value;
                        setUserId(next);
                        debouncedSetUserId(next);
                    }}
                    placeholder={`${total} carts — filter by User ID...`}
                    style={{ fontSize: "1.1rem", border: "0" }}
                />
                <select
                    value={status}
                    onChange={(e) => {
                        setStatus(e.target.value);
                        setPageNumber(1);
                    }}
                    className="px-4 py-2 rounded-lg border border-slate-300 bg-white font-sans"
                >
                    <option value="">All Statuses</option>
                    {CART_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </div>

            <TableInstance
                tableData={tableData}
                pageCount={total}
                setPageSort={setPageSort}
                pageSort={pageSort}
            />

            <div className="btn_div">
                <button
                    onClick={() => setPageNumber((n) => Number(n) - 1)}
                    disabled={pageNumber <= 1}
                    className="previous"
                >
                    Previous
                </button>
                <span className="pageindex">
                    {total < pageSize ? "Page 1" : `Page ${pageNumber} of ${maxPages}`}
                </span>
                <button
                    onClick={() => setPageNumber((n) => Number(n) + 1)}
                    disabled={pageNumber >= maxPages}
                    className="next"
                >
                    Next {maxPages}
                </button>
            </div>
        </>
    );
}

export default CartsTable;
